import { ChangeEvent, ReactElement } from 'react';

import {
  AngleDown,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraText,
  DateFormat,
  DatePicker,
  EquipUIFireflyV2Theme,
  Menu,
  MenuOption,
  MenuSelect,
  MenuSelectOption,
  PhoneNumber,
  TextInput,
} from '@equip.health/ui';
import { isEqual } from 'lodash';
import { DateTime } from 'luxon';

const TOP_LEVEL_ID = 'edit-duplicate-support-modal-row';

const { primary } = EquipUIFireflyV2Theme.colors;

type GridItemProps = {
  children: ReactElement;
  enteredValue: string;
  id: string;
  isRequired?: boolean;
  label: string;
};

export const enum ContactFieldActionTypes {
  INITIALIZE,
  UPDATE_FIRST_NAME,
  UPDATE_CHOSEN_NAME,
  UPDATE_CONTACT_LANGUAGE,
  UPDATE_LAST_NAME,
  UPDATE_GENDER,
  UPDATE_PRONOUNS,
  UPDATE_DATE_OF_BIRTH,
  UPDATE_PHONE_NUMBER,
  UPDATE_ETHNICITIES,
  UPDATE_CONTACT_PREFERENCE,
  UPDATE_TIMEZONE,
}

export const GridItem = ({
  children,
  enteredValue,
  id,
  isRequired = false,
  label,
}: GridItemProps) => (
  <ChakraSimpleGrid
    alignItems="center"
    gap="16px"
    marginY="20px"
    minHeight="48px"
    templateColumns="repeat(8, 1fr)"
  >
    <ChakraGridItem
      color="neutral.secondary"
      colSpan={2}
      fontSize="12px"
      id={`${TOP_LEVEL_ID}__${id}__row-name`}
      lineHeight="16px"
    >
      <ChakraFlex>
        {label}
        {isRequired && (
          <ChakraText color="red.500" marginLeft="4px">
            *
          </ChakraText>
        )}
      </ChakraFlex>
    </ChakraGridItem>
    <ChakraGridItem
      color="neutral.secondary"
      colSpan={3}
      fontSize="14px"
      lineHeight="28px"
      paddingRight="32px"
    >
      {children}
    </ChakraGridItem>
    <ChakraGridItem
      color="neutral.secondary"
      colSpan={3}
      fontSize="14px"
      id={`${TOP_LEVEL_ID}__${id}__entered-value`}
      lineHeight="28px"
    >
      {(enteredValue ?? '').length ? enteredValue : 'No answer'}
    </ChakraGridItem>
  </ChakraSimpleGrid>
);

type TextGridItemProps = {
  enteredValue: string;
  id: string;
  isRequired?: boolean;
  label: string;
  supportValue: string;
  updateActionTag: ContactFieldActionTypes;
  updateContactField: (ContactFieldAction) => void;
  value: string;
};

export const TextGridItem = ({
  enteredValue,
  id,
  isRequired = false,
  label,
  supportValue,
  updateActionTag,
  updateContactField,
  value,
}: TextGridItemProps) => (
  <GridItem
    enteredValue={enteredValue}
    id={id}
    isRequired={isRequired}
    label={label}
  >
    {enteredValue && enteredValue === supportValue ? (
      <>{enteredValue ?? 'No answer'}</>
    ) : (
      <TextInput
        id={`${TOP_LEVEL_ID}__${id}`}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          updateContactField({
            type: updateActionTag,
            value: event.target.value,
          });
        }}
        value={value}
      />
    )}
  </GridItem>
);

const menuRightIcon = <AngleDown fill={primary[100]} />;

type DropdownGridItemProps = {
  enteredValue: string;
  id?: string;
  isInvalid?: boolean;
  isLoading?: boolean;
  isRequired?: boolean;
  label: string;
  options: MenuOption[];
  placeholder: string;
  supportValue: string;
  updateActionTag: ContactFieldActionTypes;
  updateContactField: (ContactFieldAction) => void;
  value: string;
  withPlaceholderItem?: boolean;
};

export const DropdownGridItem = ({
  enteredValue,
  id,
  isInvalid,
  isLoading,
  isRequired,
  label,
  options,
  placeholder,
  supportValue,
  updateActionTag,
  updateContactField,
  withPlaceholderItem,
  value,
}: DropdownGridItemProps) => (
  <GridItem enteredValue={enteredValue} id={id} label={label}>
    {enteredValue && enteredValue === supportValue ? (
      <>{enteredValue ?? 'No answer'}</>
    ) : (
      <Menu
        backgroundColor="neutral.background.secondary"
        buttonContent={value}
        buttonHeight="48px"
        buttonVariant="ghost"
        formMarginBottom="10px"
        icon={menuRightIcon}
        id={`${TOP_LEVEL_ID}__${id}`}
        isFullWidth
        isInvalid={isInvalid}
        isLoading={isLoading}
        isRequired={isRequired}
        listMaxHeight="220px"
        onSelect={(value) =>
          updateContactField({ type: updateActionTag, value })
        }
        options={options}
        overflow="scroll"
        placeholder={placeholder}
        textAlign="left"
        withPlaceholderItem={withPlaceholderItem}
      />
    )}
  </GridItem>
);

type DatePickerGridItemProps = {
  enteredValue: string;
  label: string;
  supportValue: string;
  updateActionTag: ContactFieldActionTypes;
  updateContactField: (ContactFieldAction) => void;
  value: string;
};

export const DatePickerGridItem = ({
  enteredValue,
  label,
  supportValue,
  updateActionTag,
  updateContactField,
  value,
}: DatePickerGridItemProps) => (
  <GridItem enteredValue={enteredValue} id="date-of-birth" label={label}>
    {enteredValue && enteredValue === supportValue ? (
      <>{enteredValue ?? 'No answer'}</>
    ) : (
      <DatePicker
        id={`${TOP_LEVEL_ID}__date-of-birth`}
        marginBottom="10px"
        maxDate={new Date()}
        onSelect={(date) =>
          updateContactField({
            type: updateActionTag,
            value: DateTime.fromJSDate(date).toFormat(
              DateFormat.shortYearMonthDay,
            ),
          })
        }
        placeHolderText="MM/DD/YYYY"
        value={
          value
            ? DateTime.fromFormat(
                value,
                DateFormat.shortYearMonthDay,
              ).toJSDate()
            : null
        }
      />
    )}
  </GridItem>
);

type PhoneNumberGridItemProps = {
  enteredValue: string;
  isValidPhoneNumber: boolean;
  label: string;
  supportValue: string;
  updateActionTag: ContactFieldActionTypes;
  updateContactField: (ContactFieldAction) => void;
  value: string;
};

export const PhoneNumberGridItem = ({
  enteredValue,
  isValidPhoneNumber,
  label,
  supportValue,
  updateActionTag,
  updateContactField,
  value,
}: PhoneNumberGridItemProps) => {
  return (
    <GridItem
      enteredValue={enteredValue}
      id="phone-number"
      isRequired
      label={label}
    >
      {enteredValue && enteredValue === supportValue ? (
        <>{enteredValue ?? 'No answer'}</>
      ) : (
        <PhoneNumber
          id={`${TOP_LEVEL_ID}__phone-number`}
          isInvalid={!value || !isValidPhoneNumber}
          onChange={(value) => {
            updateContactField({
              type: updateActionTag,
              value,
            });
          }}
          value={value}
        />
      )}
    </GridItem>
  );
};

type MultiSelectGridItemProps = {
  enteredValue: string[];
  id: string;
  isLoading: boolean;
  label: string;
  options: MenuSelectOption[];
  placeholder: string;
  supportValue: string[];
  updateActionTag: ContactFieldActionTypes;
  updateContactField: (ContactFieldAction) => void;
  values: MenuSelectOption[];
};

export const MultiSelectGridItem = ({
  enteredValue,
  id,
  isLoading,
  label,
  options,
  placeholder,
  supportValue,
  updateActionTag,
  updateContactField,
  values,
}: MultiSelectGridItemProps) => {
  const enteredValueString =
    enteredValue?.length > 0 ? enteredValue.join(', ') : '';
  return (
    <GridItem enteredValue={enteredValueString} id={id} label={label}>
      {(enteredValue ?? []).length &&
      isEqual(
        enteredValue.toSorted((a, b) => a.localeCompare(b)),
        (supportValue ?? []).toSorted((a, b) => a.localeCompare(b)),
      ) ? (
        <>{enteredValueString ?? 'No answer'}</>
      ) : (
        <MenuSelect
          id={`${TOP_LEVEL_ID}__${id}`}
          isLoading={isLoading}
          listMaxHeight="220px"
          multiple
          onSelect={(selectedValues: MenuSelectOption[]) =>
            updateContactField({
              type: updateActionTag,
              value: selectedValues.map((value) => value.node),
            })
          }
          options={options}
          placeholder={placeholder}
          selectedValues={values ?? []}
        />
      )}
    </GridItem>
  );
};

type PatientFilterReducerAction = {
  type: ContactFieldActionTypes;
  value?: PatientContact | string[] | string;
};

export type PatientContactReducer = (
  state: PatientContact,
  action: PatientFilterReducerAction,
) => PatientContact;

export const ContactFieldReducer = (
  state: PatientContact,
  action: PatientFilterReducerAction,
): PatientContact => {
  const { type, value } = action;
  switch (type) {
    case ContactFieldActionTypes.INITIALIZE:
      return {
        ...(value as PatientContact),
      };
    case ContactFieldActionTypes.UPDATE_FIRST_NAME:
      return {
        ...state,
        firstName: value as string,
      };
    case ContactFieldActionTypes.UPDATE_LAST_NAME:
      return {
        ...state,
        lastName: value as string,
      };
    case ContactFieldActionTypes.UPDATE_CHOSEN_NAME:
      return {
        ...state,
        chosenName: value as string,
      };
    case ContactFieldActionTypes.UPDATE_GENDER:
      return {
        ...state,
        gender: value as string,
      };
    case ContactFieldActionTypes.UPDATE_PRONOUNS:
      return {
        ...state,
        pronouns: value as string[],
      };
    case ContactFieldActionTypes.UPDATE_DATE_OF_BIRTH:
      return {
        ...state,
        dateOfBirth: value as string,
      };
    case ContactFieldActionTypes.UPDATE_PHONE_NUMBER:
      return {
        ...state,
        cellPhone: value as string,
      };
    case ContactFieldActionTypes.UPDATE_ETHNICITIES:
      return {
        ...state,
        ethnicities: value as string[],
      };
    case ContactFieldActionTypes.UPDATE_CONTACT_PREFERENCE:
      return {
        ...state,
        contactPreferences: value as string[],
      };
    case ContactFieldActionTypes.UPDATE_CONTACT_LANGUAGE:
      return {
        ...state,
        languages: value as string[],
      };
    case ContactFieldActionTypes.UPDATE_TIMEZONE:
      return {
        ...state,
        timezone: value as string,
      };
    default:
      return { ...state };
  }
};
